import { invoiceReport } from '../services/invoice-report.mjs';
import React from "react";
import {
  BriefcaseBusiness,
  Users,
  CalendarDays,
  Send,
  Euro,
  TriangleAlert,
} from "lucide-react";
import { Panel, Empty } from "./ui";
import { agencyMetrics } from "../services/agency.mjs";
import { formatDate } from "../utils/calendar.mjs";
export default function AgencyOverview({
  state,
  filters,
  onNeed,
  onClient,
  onTask,
}) {
  const agencyFilters = { ...filters, owner: "" };
  const m = agencyMetrics(state, agencyFilters);
  const invoices = invoiceReport(state, agencyFilters);
  const totalHT = invoices.issued.reduce((sum,i)=>sum+(i.ht ?? 0),0);
  const missingHT = invoices.issued.filter(i=>i.ht === null).length;
  const money = (n) =>
    n.toLocaleString("fr-FR", { maximumFractionDigits: 0 }) + " €";
  const client = (id) =>
    m.clients.find((c) => c.id === id)?.name || "Établissement";
  const tiles = [
    ["Besoins à convertir", m.open.length, BriefcaseBusiness, "blue"],
    ["Démarrages à vérifier", m.starts.length, CalendarDays, "orange"],
    ["Dossiers recrutement", m.handoffs.length, Send, "purple"],
    ["Dossiers incomplets", m.missing.length, TriangleAlert, "rose"],
  ];
  return (
    <div className="agencyOverview">
      <div className="agencyHero">
        <div>
          <span className="agencyEyebrow">
            COMMERCIAL · RECRUTEMENT · EXPLOITATION
          </span>
          <h2>Constat de l’agence</h2>
          <p>
            Toute l’agence · {formatDate(filters.from)} – {formatDate(filters.to)}
          </p>
        </div>
        <span className="agencyHeroBadge">
          {m.clients.length} établissements suivis
        </span>
      </div>
      <Panel title="Chiffre d’affaires actuel de l’agence">
        <div className="agencyFinance">
          <div><span>CA facturé HT sur la période</span><strong>{money(totalHT)}</strong><small>{invoices.issued.length} facture(s) émise(s) · tous les commerciaux</small></div>
          <div><span>Factures impayées toutes dates</span><strong className="invoiceDebt">{invoices.unpaid.length}</strong><small>Encours de l’agence</small></div>
          <div><span>Reste dû HT connu</span><strong className="invoiceDebt">{money(invoices.unpaid.reduce((sum,i)=>sum+(i.remainingHT ?? 0),0))}</strong></div>
        </div>
        {(missingHT > 0 || invoices.undated > 0) && <p className="hint">Données à compléter : {missingHT} montant(s) HT et {invoices.undated} date(s) d’émission manquants. Ces données sont exclues du chiffre affiché.</p>}
        <p className="hint">Constat des factures enregistrées, hors factures annulées. Aucun devis ni montant prévisionnel dans ce chiffre.</p>
      </Panel>
      <div className="agencyTiles">
        {tiles.map(([label, value, Icon, color]) => (
          <div className={`agencyTile ${color}`} key={label}>
            <span className="agencyTileIcon">
              <Icon size={23} />
            </span>
            <div>
              <strong>{value}</strong>
              <span>{label}</span>
            </div>
          </div>
        ))}
      </div>
      <div className="agencyColumns">
        <Panel title="Démarrages proches et dates dépassées">
          <p className="hint">
            Besoins prévus d’ici 7 jours, y compris ceux dont la date est
            dépassée. La réalisation d’une mission reste à vérifier.
          </p>
          {m.starts.slice(0, 8).map((n) => (
            <button
              className="agencyNeed"
              key={n.id}
              onClick={() => onNeed(n.id)}
            >
              <div>
                <b>{client(n.clientId)}</b>
                <small>
                  {n.job} · {n.qty || "Volume inconnu"} postes · {n.type}
                </small>
                <span className="agencyContract">
                  {n.transmittedAt
                    ? "Transmis au recrutement"
                    : n.status === "signé"
                      ? "Signé · transmission à faire"
                      : n.status}
                </span>
              </div>
              <span className={`agencyDate ${n.start < m.today ? "late" : ""}`}>
                {formatDate(n.start)}
                {n.start < m.today && <small>Date dépassée</small>}
              </span>
            </button>
          ))}
          {!m.starts.length && (
            <Empty>Aucun démarrage connu dans les 7 prochains jours.</Empty>
          )}
          {m.starts.length > 8 && (
            <details>
              <summary>
                Voir les autres démarrages ({m.starts.length - 8})
              </summary>
              {m.starts.slice(8).map((n) => (
                <button
                  className="dailyOpportunity"
                  key={n.id}
                  onClick={() => onNeed(n.id)}
                >
                  {client(n.clientId)} · {n.job} · {formatDate(n.start)}
                </button>
              ))}
            </details>
          )}
        </Panel>
        <Panel title="Passage commercial → recrutement">
          <div className="agencyHandoff">
            <div>
              <strong>{m.signedPending.length}</strong>
              <span>Signés à transmettre</span>
            </div>
            <div>
              <strong>{m.missing.length}</strong>
              <span>Dossiers à compléter</span>
            </div>
          </div>
          {m.signedPending.map((n) => (
            <button
              className="dailyOpportunity"
              key={n.id}
              onClick={() => onNeed(n.id)}
            >
              <div>
                <b>{client(n.clientId)}</b>
                <small>{n.job} · Signé, transmission manquante</small>
              </div>
              <Send size={17} />
            </button>
          ))}
          {m.missing.map((n) => (
            <button
              className="dailyOpportunity"
              key={n.id}
              onClick={() => onNeed(n.needId)}
            >
              <div>
                <b>{n.client || client(n.clientId)}</b>
                <small>À compléter : {n.missingFields.join(", ")}</small>
              </div>
            </button>
          ))}
          {!m.signedPending.length && !m.missing.length && (
            <Empty>Aucun dossier à compléter ou à transmettre.</Empty>
          )}
        </Panel>
        <Panel title="Mix de contrats et potentiel de placement">
          <div className="agencyMix">
            {m.types.map((t, i) => (
              <div key={t.type} className={`mixType mix${i}`}>
                <div>
                  <b>{t.type}</b>
                  <span>
                    {t.needs} besoins · {t.positions} postes
                  </span>
                </div>
                <progress
                  aria-label={`Besoins ${t.type}`}
                  max={Math.max(1, m.active.length)}
                  value={t.needs}
                />
              </div>
            ))}
          </div>
          <p className="hint">
            Besoins actifs connus, signés inclus. Les postes recherchés ne sont
            pas des missions pourvues.
          </p>
        </Panel>
        <Panel title="Commandes et marge enregistrées">
          <div className="agencyFinance">
            <div>
              <span>Montant des commandes connu</span>
              <strong>{m.caCoverage ? money(m.ca) : "—"}</strong>
              <small>
                {m.caCoverage}/{m.orders.length} commandes renseignées
              </small>
            </div>
            <div>
              <span>Marge connue</span>
              <strong>{m.marginCoverage ? money(m.margin) : "—"}</strong>
              <small>
                {m.marginCoverage}/{m.orders.length} commandes renseignées
              </small>
            </div>
            <div className={m.unpaid ? "financeWarning" : ""}>
              <span>Impayés à échéance</span>
              <strong>{money(m.unpaid)}</strong>
              <small>{m.invoices.length} factures à suivre</small>
            </div>
          </div>
          {m.invoices.slice(0, 5).map((t) => (
            <button
              className="dailyOpportunity"
              key={t.id}
              onClick={() => onTask(t)}
            >
              <div>
                <b>{client(t.clientId)}</b>
                <small>
                  {t.invoiceReference} ·{" "}
                  {t.invoiceDue
                    ? formatDate(t.invoiceDue)
                    : "Échéance non renseignée"}
                </small>
              </div>
              <b>{money(t.remainingAmount)}</b>
            </button>
          ))}
          <details>
            <summary>Commandes sur la période ({m.orders.length})</summary>
            {m.orders.map((o, i) => (
              <button
                className="dailyOpportunity"
                key={o.id || i}
                onClick={() => onClient(o.clientId)}
              >
                <div>
                  <b>{o.client}</b>
                  <small>
                    {formatDate(o.date)} · {o.owner}
                  </small>
                </div>
                <span>
                  {o.amount == null
                    ? "CA non renseigné"
                    : money(Number(o.amount))}
                </span>
              </button>
            ))}
          </details>
        </Panel>
      </div>
    </div>
  );
}
